import { type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import type { PermissionSection } from '@xsc/shared';
import { usePermissions } from '../lib/permissions.js';

/**
 * Wraps every admin-area page with a shared tab bar so the admin tools
 * (Templates, Webhooks, Email, SMS, Customer, Users, Roles) live behind
 * a single nav entry instead of cluttering the top bar.
 *
 * Tabs are gated by the same permission matrix that gates the top nav;
 * a user who can't see Users/Roles simply doesn't get those tabs.
 */
export interface AdminTab {
  /** URL the tab routes to. */
  to: string;
  /** Display label. */
  label: string;
  /** Permission gate. When omitted the tab is admin-only. */
  section?: PermissionSection;
}

export const ADMIN_TABS: AdminTab[] = [
  { to: '/admin/templates', label: 'Templates', section: 'templates' },
  { to: '/admin/webhooks', label: 'Webhooks', section: 'webhooks' },
  { to: '/admin/email', label: 'Email', section: 'email_accounts' },
  { to: '/admin/sms', label: 'SMS', section: 'sms_providers' },
  { to: '/admin/customer', label: 'Customer', section: 'customer_config' },
  { to: '/admin/users', label: 'Users', section: 'users' },
  { to: '/admin/permissions', label: 'Roles', section: 'permissions' },
  // Admin-only (no PermissionSection) — only full admins see the tab.
  { to: '/admin/pbx-api-keys', label: 'PBX API Keys' },
  { to: '/admin/training', label: 'Training' },
  { to: '/admin/kite-pages', label: 'Pages' },
];

export function AdminShell({ children }: { children: ReactNode }): JSX.Element {
  const { can, data } = usePermissions();
  const isAdmin = data?.role === 'admin';
  const visible = ADMIN_TABS.filter((t) =>
    t.section ? can(t.section, 'view') : isAdmin,
  );
  return (
    <div className="space-y-4">
      <div className="border-b border-border -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="Admin">
          {visible.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) =>
                'border-b-2 px-3 py-2 text-sm whitespace-nowrap ' +
                (isActive
                  ? 'border-accent text-accent font-medium'
                  : 'border-transparent text-ink-muted hover:text-ink')
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <div>{children}</div>
    </div>
  );
}
